/** A document that can be settled: an invoice or a bill. */
interface Settleable {
  id: string;
  date: string;
  due: string;
  balance: string;
}

const paisa = (value: string) => Math.round(Number(value || "0") * 100);

/**
 * The part of what's applied to a bill that tax can be withheld on: its share before VAT (ADR-0031).
 * In paisa. What no bill takes is an advance, and nothing is withheld on that (ADR-0035).
 */
export function withholdingBase(
  bill: { total: string; taxable?: string },
  appliedInPaisa: number,
): number {
  const share = bill.taxable && paisa(bill.total) > 0 ? paisa(bill.taxable) / paisa(bill.total) : 1;
  return Math.round(appliedInPaisa * share);
}

/** Earliest due first. A document with no due date counts from its own date (ADR-0032). */
export function byEarliestDue<T extends Settleable>(documents: T[]): T[] {
  return [...documents].sort((a, b) => (a.due || a.date).localeCompare(b.due || b.date));
}

/**
 * What an amount settles by default (ADR-0035): each document up to its balance, earliest due first,
 * until the money runs out. A person can change it before saving.
 */
export function earliestDueFirst<T extends Settleable>(
  documents: T[],
  amountInPaisa: number,
): Record<string, string> {
  let remaining = amountInPaisa;
  const applied: Record<string, string> = {};
  for (const document of byEarliestDue(documents)) {
    const take = Math.min(remaining, paisa(document.balance));
    if (take <= 0) continue;
    applied[document.id] = (take / 100).toFixed(2);
    remaining -= take;
  }
  return applied;
}
